import type { NostrEvent } from '@nostrify/nostrify';

/** Addressable kind for Follow Party packs (compatible with following.space). */
export const FOLLOW_PACK_KIND = 39089;

export interface FollowPackMember {
  pubkey: string;
  relay?: string;
}

export interface FollowPack {
  /** `d` tag value — stable identifier scoped to author pubkey. */
  id: string;
  /** Event id of the latest known version. */
  eventId: string;
  name: string;
  description: string;
  coverImageUrl: string;
  pubkey: string;
  members: FollowPackMember[];
  createdAt: number;
}

export function isValidPackEvent(event: NostrEvent): boolean {
  if (event.kind !== FOLLOW_PACK_KIND) return false;
  const d = event.tags.find(([n]) => n === 'd')?.[1];
  if (!d) return false;
  return event.tags.some(([n, v]) => n === 'p' && typeof v === 'string' && /^[0-9a-f]{64}$/i.test(v));
}

function tagValue(event: NostrEvent, name: string): string | undefined {
  const tag = event.tags.find(([n]) => n === name);
  return tag?.[1];
}

/** Parse a kind 39089 event into a FollowPack. Backwards-compatible with old `n` title tag + JSON content description. */
export function parseFollowPackEvent(event: NostrEvent): FollowPack | null {
  try {
    if (event.kind !== FOLLOW_PACK_KIND) return null;

    const dTag = tagValue(event, 'd');
    const id = dTag || event.id;

    const title = tagValue(event, 'title') ?? tagValue(event, 'n') ?? 'Untitled Pack';
    const coverImageUrl = tagValue(event, 'image') ?? '';

    let description = tagValue(event, 'description') ?? '';
    if (!description && event.content) {
      try {
        const parsed = JSON.parse(event.content) as { description?: string };
        if (typeof parsed.description === 'string') description = parsed.description;
      } catch {
        // content is intentionally empty on new packs
      }
    }

    const members: FollowPackMember[] = event.tags
      .filter(([name, value]) => name === 'p' && typeof value === 'string' && /^[0-9a-f]{64}$/i.test(value))
      .map(([, pubkey, relay]) => ({ pubkey: pubkey.toLowerCase(), relay: relay || undefined }));

    // De-duplicate members by pubkey, keep first relay hint
    const seen = new Set<string>();
    const unique = members.filter((m) => {
      if (seen.has(m.pubkey)) return false;
      seen.add(m.pubkey);
      return true;
    });

    return {
      id,
      eventId: event.id,
      name: title.trim() || 'Untitled Pack',
      description,
      coverImageUrl,
      pubkey: event.pubkey,
      members: unique,
      createdAt: event.created_at,
    };
  } catch {
    return null;
  }
}

export function generatePackId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

/** Build tags for publishing a kind 39089 pack (caller signs + publishes). */
export function buildFollowPackTags(input: {
  id: string;
  name: string;
  description?: string;
  coverImageUrl?: string;
  members: FollowPackMember[];
}): string[][] {
  const tags: string[][] = [
    ['title', input.name.trim() || 'Untitled Pack'],
    ['d', input.id],
  ];
  if (input.coverImageUrl?.trim()) {
    tags.push(['image', input.coverImageUrl.trim()]);
  }
  if (input.description?.trim()) {
    tags.push(['description', input.description.trim()]);
  }
  // NIP-31 alt tag for clients that preview unknown kinds.
  tags.push([
    'alt',
    `Follow Party pack "${input.name.trim() || 'Untitled Pack'}" with ${input.members.length} ${input.members.length === 1 ? 'person' : 'people'}`,
  ]);
  for (const m of input.members) {
    if (!/^[0-9a-f]{64}$/i.test(m.pubkey)) continue;
    if (m.relay) tags.push(['p', m.pubkey.toLowerCase(), m.relay]);
    else tags.push(['p', m.pubkey.toLowerCase()]);
  }
  return tags;
}
